import {
  MENU_KEY_TO_SECTION,
  MENU_PATH_MATCHERS,
  NESTED_PARENT_BY_KEY,
  SIDEBAR_MENU_KEYS,
  SIDEBAR_SECTION_KEYS,
} from '../constants';

export type SidebarMenuItem = {
  key?: string;
  type?: string;
  to?: string;
  target?: string;
  labelId?: string;
  children?: Array<SidebarMenuItem | null | undefined>;
  [extra: string]: unknown;
};

export const isHomePathname = (pathname: string): boolean => {
  const path = pathname.split('?')[0].replace(/\/+$/, '');
  return /^\/[^/]+\/-$/.test(path);
};

export const pathHasSegment = (pathname: string, segment: string): boolean => {
  const path = pathname.split('?')[0];
  const needle = `/${segment}`;
  const index = path.indexOf(needle);
  if (index === -1) return false;
  const end = index + needle.length;
  return end === path.length || path.charAt(end) === '/';
};

export const getSelectedMenuKey = (pathname: string): string | null => {
  if (isHomePathname(pathname)) return SIDEBAR_MENU_KEYS.HOME;

  const matchers = [...MENU_PATH_MATCHERS].sort((left, right) => right.path.length - left.path.length);
  const match = matchers.find(({ path }) => pathHasSegment(pathname, path));
  return match ? match.key : null;
};

export const getRequiredOpenKeys = (selectedKey: string | null): string[] => {
  if (!selectedKey) return [];
  if (selectedKey === SIDEBAR_MENU_KEYS.HOME) return [];

  const keys: string[] = [];
  const section = MENU_KEY_TO_SECTION[selectedKey];
  if (section) keys.push(section);

  const nested = NESTED_PARENT_BY_KEY[selectedKey];
  if (nested) {
    keys.push(...(Array.isArray(nested) ? nested : [nested]));
  }

  return keys;
};

const SECTION_KEY_SET = new Set<string>(SIDEBAR_SECTION_KEYS);

const NESTED_KEY_SET = new Set<string>(
  Object.values(NESTED_PARENT_BY_KEY).flatMap(value => (Array.isArray(value) ? [...value] : [value])),
);

const uniqueKeys = (keys: string[]): string[] => {
  const seen = new Set<string>();
  return keys.filter(key => {
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const lastMatch = (keys: string[], predicate: (key: string) => boolean): string | undefined => {
  for (let index = keys.length - 1; index >= 0; index -= 1) {
    if (predicate(keys[index])) return keys[index];
  }
  return undefined;
};

export const isSectionMenuKey = (key: string): boolean => SECTION_KEY_SET.has(key);

export const isNestedMenuKey = (key: string): boolean => NESTED_KEY_SET.has(key);

export const getNestedSiblingKeys = (key: string): string[] => {
  const section = MENU_KEY_TO_SECTION[key];
  if (!section) return [key];
  return Array.from(NESTED_KEY_SET).filter(
    nestedKey => MENU_KEY_TO_SECTION[nestedKey] === section,
  );
};

export const parseStoredOpenKeys = (stored: unknown): string[] => {
  if (!Array.isArray(stored)) return [];
  return stored.filter((key): key is string => typeof key === 'string' && isSectionMenuKey(key));
};

export const persistableOpenKeys = (openKeys: string[], requiredOpenKeys: string[]): string[] => {
  const required = new Set(requiredOpenKeys);
  return uniqueKeys(openKeys.filter(key => isSectionMenuKey(key) && !required.has(key)));
};

export const resolveOpenKeys = (userOpenKeys: string[], requiredOpenKeys: string[]): string[] => {
  const required = uniqueKeys(requiredOpenKeys);
  const requiredSet = new Set(required);

  const extraSection = lastMatch(
    userOpenKeys,
    key => isSectionMenuKey(key) && !requiredSet.has(key),
  );

  const extraNested = lastMatch(userOpenKeys, key => isNestedMenuKey(key) && !requiredSet.has(key));
  const requiredNested = required.filter(isNestedMenuKey);
  const extraNestedConflicts = Boolean(
    extraNested &&
      requiredNested.some(nestedKey => getNestedSiblingKeys(nestedKey).includes(extraNested)),
  );

  return uniqueKeys([
    ...required,
    ...(extraSection ? [extraSection] : []),
    ...(extraNested && !extraNestedConflicts ? [extraNested] : []),
  ]);
};

const isDivider = (item: SidebarMenuItem): boolean => item.type === 'divider';

const squeezeDividers = (items: SidebarMenuItem[]): SidebarMenuItem[] => {
  const squeezed: SidebarMenuItem[] = [];
  items.forEach(item => {
    if (!isDivider(item)) {
      squeezed.push(item);
      return;
    }
    if (squeezed.length === 0) return;
    if (isDivider(squeezed[squeezed.length - 1])) return;
    squeezed.push(item);
  });
  if (squeezed.length > 0 && isDivider(squeezed[squeezed.length - 1])) {
    squeezed.pop();
  }
  return squeezed;
};

/**
 * A dense column of icons is unreadable once the labels are gone, so the rail
 * keeps only Home and the screen in view; everything else lives behind the
 * expand affordance.
 */
export const getRailMenuItems = (
  items: SidebarMenuItem[],
  selectedKey: string | null,
): SidebarMenuItem[] => {
  const found = new Map<string, SidebarMenuItem>();

  const walk = (nodes: Array<SidebarMenuItem | null | undefined>): void => {
    nodes.forEach(node => {
      if (!node || isDivider(node)) return;
      if (node.key && (node.key === SIDEBAR_MENU_KEYS.HOME || node.key === selectedKey)) {
        found.set(node.key, { ...node, children: undefined });
      }
      if (node.children) walk(node.children);
    });
  };

  walk(items);

  return [SIDEBAR_MENU_KEYS.HOME, selectedKey]
    .filter((key, index, keys): key is string => Boolean(key) && keys.indexOf(key) === index)
    .map(key => found.get(key))
    .filter((item): item is SidebarMenuItem => Boolean(item));
};

export type MenuNavigationEntry = {
  to?: string;
  target?: string;
  labelId?: string;
};

/**
 * In the rail the label is clipped away, so the row itself has to navigate.
 * Route metadata lives on each item so navigation does not depend on React labels.
 */
export const getMenuNavigationMap = (
  items: SidebarMenuItem[],
): Record<string, MenuNavigationEntry> => {
  const map: Record<string, MenuNavigationEntry> = {};

  const walk = (nodes: Array<SidebarMenuItem | null | undefined>): void => {
    nodes.forEach(node => {
      if (!node || isDivider(node)) return;
      if (node.key) {
        map[node.key] = { to: node.to, target: node.target, labelId: node.labelId };
      }
      if (node.children) walk(node.children);
    });
  };

  walk(items);
  return map;
};

export const compactMenuItems = (
  items: Array<SidebarMenuItem | null | undefined | false>,
): SidebarMenuItem[] => {
  const compacted = items
    .filter((item): item is SidebarMenuItem => Boolean(item))
    .map(item => {
      if (!item.children) return item;
      const children = compactMenuItems(item.children);
      if (children.length === 0) return null;
      return { ...item, children };
    })
    .filter((item): item is SidebarMenuItem => Boolean(item));

  return squeezeDividers(compacted);
};
